import {
  Component,
  h,
  Prop,
  State,
  Event,
  EventEmitter,
  Watch
} from "@stencil/core";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { ChEditCustomEvent } from "@genexus/chameleon-controls-library";
import { FileUploadState } from "../types";

const FILE_ICON = getIconPath({
  category: "gemini-tools",
  name: "file",
  colorType: "primary"
});

const EDIT_ICON = getIconPath({
  category: "gemini-tools",
  name: "edit",
  colorType: "primary"
});

const DELETE_ICON = getIconPath({
  category: "gemini-tools",
  name: "delete",
  colorType: "primary"
});

const CANCEL_ICON = getIconPath({
  category: "gemini-tools",
  name: "close",
  colorType: "primary"
});

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/icon",
  "components/edit",
  "utils/typography",
  "utils/spacing",
  "utils/form",
  "utils/layout",
  "utils/elevation"
];

@Component({
  tag: "gx-ide-file-item",
  styleUrl: "file-item.scss",
  shadow: true
})
export class FileItem {
  /** File state containing upload information */
  @Prop() readonly file!: FileUploadState;

  /** Whether the file name can be edited */
  @Prop() readonly editable: boolean = false;

  /** Component locale strings */
  @Prop() readonly componentLocale?: any;

  /** Callback when a file is deleted */
  @Prop() readonly fileDeletedCallback?: (fileId: string) => Promise<void>;

  /** Callback when a file deletion error occurs */
  @Prop() readonly fileDeletionErrorCallback?: (data: {
    fileName: string;
    error: string;
  }) => Promise<void>;

  /** Updates current name when file prop changes */
  @Watch("file")
  handleFileChange(newFile: FileUploadState) {
    this.currentName = newFile.name;
  }

  @State() isEditing: boolean = false;
  @State() currentName: string;

  /** Emitted when the file name is changed */
  @Event() fileNameChange: EventEmitter<{ id: string; name: string }>;

  /** Emitted when the file is deleted */
  @Event() fileDelete: EventEmitter<string>;

  /** Emitted when the file upload is cancelled */
  @Event() fileCancel: EventEmitter<string>;

  componentWillLoad() {
    this.currentName = this.file.name;
  }

  #handleNameChange = (event: ChEditCustomEvent<string> | InputEvent) => {
    const newName = (event as CustomEvent<string>).detail;

    if (newName && newName !== this.currentName) {
      this.currentName = newName;
      this.fileNameChange.emit({
        id: this.file.id,
        name: newName
      });
    }
  };

  #handleBlur = () => {
    this.isEditing = false;
  };

  /** Handles file deletion */
  #handleDelete = () => {
    this.fileDelete.emit(this.file.id);
  };

  /** Handles upload cancellation */
  #handleCancelClick = (e: MouseEvent) => {
    e.preventDefault();
    this.fileCancel.emit(this.file.id);
  };

  /** Starts file name editing */
  #startEditing = () => {
    if (this.editable) {
      this.isEditing = true;
    }
  };

  render() {

    const displayItemActions = !this.file.pending;
    const displayProgressContainer = this.file.pending;
    const labelId = "progress-label-" + this.file.id;

    let liAriaLabel: string;
    if (this.file.pending) {
      liAriaLabel = this.componentLocale?.fileItem?.uploadingAriaLabel
        ?.replace("{fileName}", this.currentName)
        ?.replace("{fileProgress}", this.file.progress.toString()) || this.currentName;
    } else if (this.file.remoteId) {
      liAriaLabel = this.componentLocale?.fileItem?.uploadedSuccessfullyAriaLabel
        ?.replace("{fileName}", this.currentName) || this.currentName;
    } else {
      liAriaLabel = this.currentName;
    }

    return (
      <li class={{
        "file-item": true,
        "file-item--without-actions": !displayItemActions,
        "file-item--with-actions": displayItemActions
      }}
        aria-label={liAriaLabel}
      >

        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <div class="file-item__icon">
          <ch-image disabled={!this.file.pending} src={FILE_ICON} class="icon-m" />
        </div>

        <div class="file-item__name">
          {this.isEditing ? (
            <ch-edit
              accessibleName={this.componentLocale?.fileItem?.editNamePlaceholderAriaLabel
                ?.replace("{fileName}", this.currentName)}
              value={this.currentName}
              autoFocus={true}
              onInput={this.#handleNameChange}
              onBlur={this.#handleBlur}
              class="input"
              id={labelId}
            />
          ) : (
            <span class="label" id={labelId}>{this.currentName}</span>
          )}
        </div>

        {displayItemActions ? (
          <div class="file-item__actions">
            {!this.file.pending && this.editable && (
              <button
                class="button-tertiary button-icon-only"
                type="button"
                onClick={this.#startEditing}
              >
                <ch-image src={EDIT_ICON} class="icon-m" />
              </button>
            )}
            {!this.file.pending && (
              <button
                class="button-tertiary button-icon-only"
                type="button"
                onClick={this.#handleDelete}
                aria-label="Delete file"
              >
                <ch-image src={DELETE_ICON} class="icon-m" />
              </button>
            )}
          </div>
        ) : (
          null
        )}

        {displayProgressContainer && (
          <div class="file-item__progress-container">
            <div class="file-item__progress-header">
              <span class="file-item__uploading-text typography-body-italic-s">
                Uploading {this.currentName}...
              </span>
              <button
                aria-label={this.componentLocale?.fileItem?.cancelUploadButtonAriaLabel
                  ?.replace("{fileName}", this.currentName) || "Cancel upload"}
                class="button-tertiary button-icon-only file-item__cancel-upload"
                type="button"
                onClick={this.#handleCancelClick}
              >
                <ch-image src={CANCEL_ICON} class="icon-m" />
              </button>
            </div>
            <div class="file-item__progress-bar">
              <progress
                class="elevation-1"
                value={this.file.progress}
                max="100"
                aria-labelledby={labelId}
              />
            </div>
          </div>
        )}
      </li>
    );
  }
}
